import { useState, useEffect, useCallback, useRef } from 'react';

const BASE = '/api';
const getToken = () => localStorage.getItem('ozon_token');

function api(method, path, body) {
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

function fmtDt(s) {
  if (!s) return '—';
  return new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Spinner({ sm }) {
  return <div className={`${sm ? 'w-3 h-3' : 'w-5 h-5'} border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin`} />;
}

// ─────────────────────────────────────────────
// TAB 1: GİDEN TALEPLER (StockRequests content)
// ─────────────────────────────────────────────

const SENT_STATUS = {
  draft:     { label: 'Taslak',     color: 'bg-gray-100 text-gray-600' },
  sent:      { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700' },
  approved:  { label: 'Onaylandı',  color: 'bg-green-100 text-green-700' },
  rejected:  { label: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  failed:    { label: 'Başarısız',  color: 'bg-orange-100 text-orange-700' },
  cancelled: { label: 'İptal',      color: 'bg-gray-100 text-gray-400' },
};

function Badge({ status }) {
  const s = SENT_STATUS[status] || SENT_STATUS.draft;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.color}`}>{s.label}</span>;
}

function SourceSettingsModal({ source, onClose, onSaved }) {
  const [form, setForm] = useState({
    endpoint_url: source.endpoint_url || '',
    api_key:      source.api_key || '',
    callback_key: source.callback_key || '',
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await api('PATCH', `/stock-requests/sources/${encodeURIComponent(source.name)}`, form);
      onSaved();
      onClose();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-1">{source.name} — Bağlantı Ayarları</h2>
        <p className="text-xs text-gray-400 mb-5">Karşı tarafın backend adresi ve API key'leri</p>
        {[
          { key: 'endpoint_url', label: 'Endpoint URL', placeholder: 'http://localhost:5002' },
          { key: 'api_key',      label: 'API Key',      placeholder: 'gizli-anahtar' },
          { key: 'callback_key', label: 'Callback Key', placeholder: 'callback-anahtar' },
        ].map(f => (
          <div key={f.key} className="mb-3">
            <label className="text-xs font-semibold text-gray-500 block mb-1">{f.label}</label>
            <input
              value={form[f.key]}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
            />
          </div>
        ))}
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2 text-sm hover:bg-gray-50">İptal</button>
          <button onClick={save} disabled={saving} className="flex-1 bg-blue-600 text-white rounded-xl py-2 text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestDetail({ request, onClose, onUpdate }) {
  const [sending,    setSending]    = useState(false);
  const [cancelling, setCancelling] = useState(false);

  async function send() {
    setSending(true);
    try {
      const updated = await api('POST', `/stock-requests/${request.id}/send`);
      onUpdate(updated);
      if (updated.warning) alert('⚠️ ' + updated.warning);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setSending(false); }
  }

  async function cancel() {
    if (!window.confirm('Talep iptal edilsin mi?')) return;
    setCancelling(true);
    try {
      const updated = await api('POST', `/stock-requests/${request.id}/cancel`);
      onUpdate(updated);
      onClose();
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setCancelling(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg font-bold text-gray-900">{request.source_name} Stok Talebi</h2>
              <Badge status={request.status} />
            </div>
            <p className="text-xs text-gray-400">
              {request.session_note || 'Oturum'} · {fmtDt(request.created_at)}
              {request.created_by && ` · ${request.created_by}`}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-gray-600 text-xl p-1">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-400 border-b border-gray-100">
                <th className="text-left py-2 font-medium">Ürün Kodu</th>
                <th className="text-left py-2 font-medium">Ürün Adı</th>
                <th className="text-right py-2 font-medium">Adet</th>
              </tr>
            </thead>
            <tbody>
              {request.items.map((item, i) => (
                <tr key={i} className="border-b border-gray-50">
                  <td className="py-2.5 pr-4"><div className="font-mono text-xs font-medium text-gray-800">{item.offer_id}</div></td>
                  <td className="py-2.5 pr-4"><div className="text-xs text-gray-500 truncate max-w-[200px]">{item.name}</div></td>
                  <td className="py-2.5 text-right font-semibold text-gray-800">{item.qty}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {request.response_note && (
            <div className={`mt-4 rounded-xl px-4 py-3 text-sm border ${
              request.status === 'approved' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <p className="font-semibold mb-0.5">Karşı taraf notu:</p>
              <p>{request.response_note}</p>
              {request.responded_at && <p className="text-xs opacity-60 mt-1">{fmtDt(request.responded_at)}</p>}
            </div>
          )}
        </div>
        {request.status === 'draft' && (
          <div className="p-5 border-t border-gray-100 flex gap-2">
            <button onClick={cancel} disabled={cancelling}
              className="border border-gray-200 text-gray-600 rounded-xl px-4 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
              İptal Et
            </button>
            <button onClick={send} disabled={sending}
              className="flex-1 bg-blue-600 text-white rounded-xl px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 font-medium">
              {sending ? 'Gönderiliyor...' : `${request.source_name}'e Gönder`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function GidenTalepler() {
  const [requests, setRequests] = useState([]);
  const [sources,  setSources]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [detail,   setDetail]   = useState(null);
  const [settings, setSettings] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [reqs, srcs] = await Promise.all([
        api('GET', '/stock-requests'),
        api('GET', '/stock-requests/sources'),
      ]);
      setRequests(reqs);
      setSources(srcs);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function updateRequest(updated) {
    setRequests(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
    if (detail?.id === updated.id) setDetail(d => ({ ...d, ...updated }));
  }

  const counts = Object.fromEntries(
    Object.keys(SENT_STATUS).map(s => [s, requests.filter(r => r.status === s).length])
  );

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-gray-500">Tedarikçilere gönderilen stok düşüm talepleri</p>
        <div className="flex gap-2">
          {sources.map(s => (
            <button key={s.name} onClick={() => setSettings(s)}
              className="text-xs border border-gray-200 text-gray-500 hover:border-gray-300 px-3 py-1.5 rounded-lg">
              ⚙ {s.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
        {Object.entries(SENT_STATUS).map(([key, s]) => (
          <div key={key} className={`rounded-xl px-3 py-2 text-center border ${counts[key] > 0 ? s.color.replace('text-', 'border-').replace(/\d+$/, '200') : 'border-gray-100 bg-gray-50'}`}>
            <div className={`text-lg font-bold ${counts[key] > 0 ? s.color.split(' ')[1] : 'text-gray-300'}`}>{counts[key]}</div>
            <div className="text-xs text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16 text-gray-300">Yükleniyor...</div>
      ) : requests.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <div className="text-4xl mb-3">📬</div>
          <p className="text-sm font-medium">Henüz talep yok</p>
          <p className="text-xs mt-1">Tedarik oturumu detayından "Stok Talebi Oluştur" butonunu kullan</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          {requests.map((r, i) => (
            <button key={r.id} onClick={() => setDetail(r)}
              className={`w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors ${i < requests.length - 1 ? 'border-b border-gray-50' : ''}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-gray-900 text-sm">{r.source_name}</span>
                  <Badge status={r.status} />
                  <span className="text-xs text-gray-400">{r.items?.length || 0} ürün</span>
                </div>
                <p className="text-xs text-gray-400 truncate">
                  {r.session_note || 'Oturum'} · {fmtDt(r.created_at)}
                  {r.created_by ? ` · ${r.created_by}` : ''}
                </p>
              </div>
              <div className="text-gray-300 flex-shrink-0">›</div>
            </button>
          ))}
        </div>
      )}

      {detail   && <RequestDetail request={detail} onClose={() => setDetail(null)} onUpdate={updateRequest} />}
      {settings && <SourceSettingsModal source={settings} onClose={() => setSettings(null)} onSaved={load} />}
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 2: GELEN TALEPLER (IncomingRequests content)
// ─────────────────────────────────────────────

const RECV_STATUS = {
  pending:  { label: 'Bekliyor',   color: 'bg-amber-50 text-amber-700 border-amber-200' },
  approved: { label: 'Onaylandı',  color: 'bg-green-50 text-green-700 border-green-200' },
  rejected: { label: 'Reddedildi', color: 'bg-red-50 text-red-700 border-red-200' },
};

// Sadece dropdown'dan seçim yapılır, serbest metin kabul edilmez
function SkuSearch({ value, onChange, alwaysUp = false }) {
  const [query,    setQuery]    = useState('');
  const [results,  setResults]  = useState([]);
  const [open,     setOpen]     = useState(false);
  const [dropUp,   setDropUp]   = useState(alwaysUp);
  const [selected, setSelected] = useState(null); // { offer_id, name }
  const inputRef = useRef(null);

  // value dışarıdan değişirse seçimi sıfırla
  useEffect(() => {
    if (!value) { setSelected(null); setQuery(''); }
  }, [value]);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api('GET', `/supply-map/my-skus?search=${encodeURIComponent(query)}`)
        .then(r => setResults(r))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  function handleFocus() {
    if (!alwaysUp && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropUp(rect.bottom + 220 > window.innerHeight);
    }
    setOpen(true);
  }

  function handleBlur() {
    setTimeout(() => {
      setOpen(false);
      setQuery(''); // seçilmeden kapandıysa ara kutusunu temizle
    }, 150);
  }

  function select(row) {
    setSelected(row);
    setQuery('');
    setOpen(false);
    onChange(row.offer_id);
  }

  function clear(e) {
    e.stopPropagation();
    setSelected(null);
    setQuery('');
    onChange('');
  }

  // Seçili ürün gösterimi
  if (selected || value) {
    const displayId = selected?.offer_id || value;
    const displayName = selected?.name;
    return (
      <div className="flex items-center gap-1 border border-green-300 bg-green-50 rounded-lg px-2.5 py-1.5 text-xs">
        <div className="flex-1 min-w-0">
          <div className="font-mono font-medium text-green-800 truncate">{displayId}</div>
          {displayName && <div className="text-green-600 truncate text-[10px]">{displayName}</div>}
        </div>
        <button onMouseDown={clear} className="text-green-400 hover:text-red-400 flex-shrink-0 ml-1" title="Seçimi kaldır">✕</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="Ara ve seç..."
        className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-400"
      />
      {open && results.length > 0 && (
        <div className={`absolute z-[999] left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto
          ${dropUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
          {results.map(r => (
            <button key={r.offer_id} onMouseDown={() => select(r)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50 last:border-0">
              <div className="font-mono text-gray-800">{r.offer_id}</div>
              {r.name && <div className="text-gray-400 truncate">{r.name}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WarehouseAssignModal({ items, warehouses, onSave, onCancel }) {
  const [selections, setSelections] = useState(() => {
    const init = {};
    items.forEach(it => { init[it.offer_id] = ''; });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const allSelected = items.every(it => selections[it.offer_id]);

  async function save() {
    setSaving(true);
    try {
      for (const it of items) {
        const wid = selections[it.offer_id];
        if (!wid) continue;
        const wh = warehouses.find(w => String(w.warehouse_id) === wid);
        await api('POST', '/product-warehouse', { offer_id: it.offer_id, warehouse_id: parseInt(wid), warehouse_name: wh?.name });
      }
      onSave();
    } catch (e) { alert('Kayıt hatası: ' + e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <h2 className="font-bold text-gray-900 text-lg mb-1">Depo Eşleştirmesi Gerekli</h2>
        <p className="text-sm text-gray-500 mb-4">Ozon stoğu güncellenebilmesi için aşağıdaki ürünlere depo ataması yapılmalı.</p>
        <div className="flex flex-col gap-3 mb-5">
          {items.map(it => (
            <div key={it.offer_id} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-mono text-xs text-gray-700 truncate">{it.offer_id}</p>
                {it.name && <p className="text-xs text-gray-400 truncate">{it.name}</p>}
              </div>
              <select value={selections[it.offer_id] || ''} onChange={e => setSelections(s => ({ ...s, [it.offer_id]: e.target.value }))}
                className={`border rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-400 w-48 ${selections[it.offer_id] ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                <option value="">— depo seç —</option>
                {warehouses.map(w => <option key={w.warehouse_id} value={String(w.warehouse_id)}>{w.name}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={saving} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50">İptal</button>
          <button onClick={save} disabled={!allSelected || saving} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Kaydediliyor...' : 'Kaydet ve Onayla'}
          </button>
        </div>
      </div>
    </div>
  );
}

function IncomingRequestCard({ req, onAction }) {
  const [items,       setItems]       = useState(req.items || []);
  const [note,        setNote]        = useState('');
  const [saving,      setSaving]      = useState(false);
  const [acting,      setActing]      = useState(false);
  const [open,        setOpen]        = useState(req.status === 'pending');
  const [dirty,       setDirty]       = useState(false);
  const [assignModal, setAssignModal] = useState(null);

  const sm = RECV_STATUS[req.status] || { label: req.status, color: 'bg-gray-50 text-gray-600 border-gray-200' };

  function updateMapping(idx, mappedOfferId) {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, mapped_offer_id: mappedOfferId, auto_mapped: false } : it));
    setDirty(true);
  }

  async function saveMapping() {
    setSaving(true);
    try {
      await api('PATCH', `/incoming-requests/${req.id}/items`, { items });
      // supply_sku_map'e de kaydet (gelecekteki otomatik eşleştirme için)
      for (const it of items) {
        if (it.mapped_offer_id && it.offer_id) {
          try {
            await api('POST', '/supply-map', {
              source_name:          req.source_name || 'BSW',
              counterpart_offer_id: it.offer_id,
              counterpart_name:     it.name || null,
              my_offer_id:          it.mapped_offer_id,
            });
          } catch (_) {}
        }
      }
      setDirty(false);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setSaving(false); }
  }

  async function act(action) {
    if (dirty) { alert('Önce mapping kaydet'); return; }
    setActing(true);
    try {
      if (action === 'approve') {
        const token = getToken();
        const r = await fetch(`${BASE}/incoming-requests/${req.id}/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ note }),
        });
        const d = await r.json().catch(() => ({}));
        if (r.status === 422 && d.error === 'missing_warehouse') {
          const warehouses = await api('GET', '/ozon-stock/warehouses').catch(() => []);
          const missingItems = d.missing.map(offerId => {
            const item = items.find(it => (it.mapped_offer_id || it.offer_id) === offerId);
            return { offer_id: offerId, name: item?.name || '', warehouse_id: null };
          });
          setActing(false);
          setAssignModal({ items: missingItems, warehouses });
          return;
        }
        if (!r.ok) throw new Error(d.error || r.statusText);
        onAction();
      } else {
        await api('POST', `/incoming-requests/${req.id}/${action}`, { note });
        onAction();
      }
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setActing(false); }
  }

  async function handleAssignSaved() {
    setAssignModal(null);
    setActing(true);
    try {
      await api('POST', `/incoming-requests/${req.id}/approve`, { note });
      onAction();
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setActing(false); }
  }

  const mappingComplete = items.every(it => it.mapped_offer_id || it.offer_id);

  return (
    <>
    {assignModal && (
      <WarehouseAssignModal
        items={assignModal.items}
        warehouses={assignModal.warehouses}
        onSave={handleAssignSaved}
        onCancel={() => setAssignModal(null)}
      />
    )}
    <div className={`bg-white border rounded-2xl shadow-sm ${req.status === 'pending' ? 'border-amber-200' : 'border-gray-100'}`}>
      <div className="flex items-center justify-between px-4 py-3 cursor-pointer select-none rounded-2xl" onClick={() => setOpen(o => !o)}>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${sm.color}`}>{sm.label}</span>
          <span className="text-sm font-medium text-gray-800">#{req.id} · {req.source_name || 'Dış kaynak'}</span>
          <span className="text-xs text-gray-400">{items.length} ürün</span>
          {req.session_note && <span className="text-xs text-gray-400 hidden sm:block">— {req.session_note}</span>}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{fmtDt(req.created_at)}</span>
          <span>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-100 px-4 py-4">
          <div className="mb-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-xs text-gray-500 font-semibold border-b border-gray-100">
                  <th className="text-left py-2 w-2/5">Gelen Ürün (Karşı Taraf)</th>
                  <th className="text-center py-2 w-16">Adet</th>
                  <th className="text-left py-2">→ Bendeki Eşleşme (Ozon)</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-2 pr-3">
                      <div className="font-mono text-xs text-gray-700">{item.offer_id}</div>
                      {item.name && <div className="text-[10px] text-gray-400 truncate max-w-[160px]">{item.name}</div>}
                    </td>
                    <td className="py-2 text-center font-bold text-gray-900 pr-3">{item.qty}</td>
                    <td className="py-2 pl-3">
                      {req.status === 'pending' ? (
                        <div className="flex items-center gap-1.5">
                          <div className="flex-1">
                            <SkuSearch
                              value={item.mapped_offer_id}
                              onChange={val => updateMapping(i, val)}
                              alwaysUp={i >= items.length - 2}
                            />
                          </div>
                          {item.auto_mapped && (
                            <span title="SKU tablosundan otomatik eşleşti"
                              className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 border border-blue-200">
                              🔗 oto
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-xs text-blue-700">{item.mapped_offer_id || item.offer_id}</span>
                          {item.auto_mapped && (
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 border border-blue-200">🔗 oto</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {req.status === 'pending' && (
            <>
              {dirty && (
                <div className="flex justify-end mb-3">
                  <button onClick={saveMapping} disabled={saving}
                    className="px-4 py-1.5 bg-gray-800 text-white rounded-xl text-xs font-medium hover:bg-gray-900 disabled:opacity-50">
                    {saving ? 'Kaydediliyor...' : '💾 Mapping Kaydet'}
                  </button>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <input value={note} onChange={e => setNote(e.target.value)}
                  placeholder="Not ekle (isteğe bağlı)..."
                  className="border border-gray-200 rounded-xl px-3 py-2 text-sm flex-1 focus:outline-none focus:border-blue-400" />
                <div className="flex gap-2">
                  <button onClick={() => act('approve')} disabled={acting || !mappingComplete}
                    className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 disabled:opacity-50">
                    ✓ Onayla
                  </button>
                  <button onClick={() => act('reject')} disabled={acting}
                    className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 disabled:opacity-50">
                    ✗ Reddet
                  </button>
                </div>
              </div>
              {!mappingComplete && (
                <p className="text-xs text-amber-600 mt-2">⚠️ Tüm ürünler eşleştirilmeden onaylanamaz</p>
              )}
            </>
          )}
          {req.status !== 'pending' && (
            <div className="text-xs text-gray-400">
              Yanıtlandı: {fmtDt(req.responded_at)}
              {req.note && <span className="ml-2 text-gray-600">· {req.note}</span>}
            </div>
          )}
        </div>
      )}
    </div>
    </>
  );
}

function GelenTalepler({ onPendingChange }) {
  const [requests, setRequests] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter ? `?status=${filter}` : '';
      const data = await api('GET', `/incoming-requests${params}`);
      const list = Array.isArray(data) ? data : [];
      setRequests(list);
      if (filter === 'pending' && onPendingChange) onPendingChange(list.length);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setLoading(false); }
  }, [filter, onPendingChange]);

  useEffect(() => { load(); }, [load]);

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="max-w-4xl">
      <p className="text-sm text-gray-500 mb-5">Dışarıdan gelen stok düşüm talepleri — eşleştir ve onayla</p>

      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1 border-b border-gray-200">
          {[['pending','Bekleyenler'],['approved','Onaylananlar'],['rejected','Reddedilenler'],['','Tümü']].map(([val, label]) => (
            <button key={val} onClick={() => setFilter(val)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
                filter === val ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>
              {label}
              {val === 'pending' && pendingCount > 0 && (
                <span className="ml-1.5 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-xl text-sm font-medium disabled:opacity-50">
          <span className={loading ? 'animate-spin inline-block' : ''}>↻</span> Yenile
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">Kayıt bulunamadı</div>
      ) : (
        <div className="flex flex-col gap-3">
          {requests.map(r => <IncomingRequestCard key={r.id} req={r} onAction={load} />)}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB 3: SKU EŞLEŞTİRME (SkuMapping content)
// ─────────────────────────────────────────────

function ProductSelect({ value, onChange, mySkus, placeholder = 'Ürün seç...' }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = mySkus.find(s => s.offer_id === value);
  const filtered = search
    ? mySkus.filter(s => s.offer_id.toLowerCase().includes(search.toLowerCase()) || (s.name || '').toLowerCase().includes(search.toLowerCase()))
    : mySkus;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => { setOpen(o => !o); setSearch(''); }}
        className={`w-full text-left px-2.5 py-1.5 border rounded-lg text-xs font-mono flex items-center justify-between gap-1 transition-colors
          ${value ? 'border-green-300 bg-green-50 text-green-800' : 'border-gray-200 bg-white text-gray-400'}`}>
        <span className="truncate">{selected ? selected.offer_id : placeholder}</span>
        <span className="text-gray-300 flex-shrink-0">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 top-full mt-1 left-0 w-72 bg-white border border-gray-200 rounded-xl shadow-xl">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Ara..."
              className="w-full text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {value && (
              <button onClick={() => { onChange(''); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs text-red-500 hover:bg-red-50 border-b border-gray-50">
                ✕ Seçimi kaldır
              </button>
            )}
            {filtered.slice(0, 100).map(s => (
              <button key={s.offer_id} onClick={() => { onChange(s.offer_id); setOpen(false); }}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${s.offer_id === value ? 'bg-blue-50 text-blue-700' : ''}`}>
                <div className="text-xs font-mono font-medium text-gray-800 truncate">{s.offer_id}</div>
                {s.name && <div className="text-xs text-gray-400 truncate">{s.name}</div>}
              </button>
            ))}
            {filtered.length === 0 && <p className="text-xs text-gray-400 p-3 text-center">Sonuç yok</p>}
          </div>
        </div>
      )}
    </div>
  );
}

function CounterpartCell({ row, onSave }) {
  const [editing, setEditing] = useState(false);
  const [code,    setCode]    = useState(row.counterpart_offer_id || '');
  const [name,    setName]    = useState(row.counterpart_name || '');
  const [saving,  setSaving]  = useState(false);

  async function save() {
    if (!code.trim()) { setEditing(false); setCode(row.counterpart_offer_id || ''); setName(row.counterpart_name || ''); return; }
    setSaving(true);
    try {
      const updated = await api('PATCH', `/supply-map/${row.id}`, {
        counterpart_offer_id: code.trim(),
        counterpart_name: name.trim() || null,
      });
      onSave(updated);
      setEditing(false);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setSaving(false); }
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1 min-w-[160px]">
        <input autoFocus value={code} onChange={e => setCode(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setCode(row.counterpart_offer_id || ''); setName(row.counterpart_name || ''); } }}
          placeholder="Ürün kodu"
          className="border border-blue-300 rounded px-2 py-1 text-xs font-mono focus:outline-none w-full" />
        <input value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="Ürün adı (isteğe bağlı)"
          className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none w-full" />
        <div className="flex gap-1">
          <button onMouseDown={save} disabled={saving}
            className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? '...' : 'Kaydet'}
          </button>
          <button onMouseDown={() => { setEditing(false); setCode(row.counterpart_offer_id || ''); setName(row.counterpart_name || ''); }}
            className="text-[10px] border border-gray-200 text-gray-500 px-2 py-0.5 rounded hover:bg-gray-50">
            İptal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => setEditing(true)} title="Düzenlemek için tıkla"
      className="cursor-pointer group min-w-[120px]">
      {row.counterpart_offer_id ? (
        <div>
          <div className="font-mono font-medium text-gray-800 group-hover:text-blue-600 transition-colors flex items-center gap-1">
            {row.counterpart_offer_id}
            <span className="opacity-0 group-hover:opacity-100 text-[10px] text-blue-400 transition-opacity">✎</span>
          </div>
          {row.counterpart_name && <div className="text-gray-400 truncate max-w-[200px] text-xs">{row.counterpart_name}</div>}
        </div>
      ) : (
        <span className="text-gray-300 italic text-xs group-hover:text-blue-400 transition-colors">+ ekle</span>
      )}
    </div>
  );
}

function SkuEslestirme() {
  const [sources,   setSources]   = useState([]);
  const [filter,    setFilter]    = useState(null); // null = tümü
  const [allRows,   setAllRows]   = useState([]);
  const [mySkus,    setMySkus]    = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [savingId,  setSavingId]  = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [addForm,   setAddForm]   = useState(null);
  const [addSaving, setAddSaving] = useState(false);
  const fileRef = useRef(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Tüm kaynakların mapping'lerini çek (sadece counterpart_offer_id dolu olanlar)
      const src = await api('GET', '/scan/sources');
      setSources(src);
      const names = src.map(s => s.name);
      if (!names.length) { setAllRows([]); return; }
      const results = await Promise.all(
        names.map(n => api('GET', `/supply-map?source=${encodeURIComponent(n)}`).catch(() => []))
      );
      const merged = results.flat().filter(r => r.counterpart_offer_id);
      setAllRows(merged);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadAll();
    api('GET', '/supply-map/my-skus').then(setMySkus).catch(console.error);
  }, [loadAll]);

  const [matchFilter, setMatchFilter] = useState('matched'); // 'matched' | 'missing' | 'all'

  const sourceFiltered = filter ? allRows.filter(r => r.source_name === filter) : allRows;
  const rows = matchFilter === 'matched'
    ? sourceFiltered.filter(r => r.my_offer_id)
    : matchFilter === 'missing'
    ? sourceFiltered.filter(r => !r.my_offer_id)
    : sourceFiltered;

  const matchedCount = sourceFiltered.filter(r => r.my_offer_id).length;
  const missingCount = sourceFiltered.filter(r => !r.my_offer_id).length;

  async function saveMyOfferId(row, myOfferId) {
    setSavingId(row.id);
    try {
      const updated = await api('PATCH', `/supply-map/${row.id}`, {
        my_offer_id: myOfferId || null,
        my_name: mySkus.find(s => s.offer_id === myOfferId)?.name || null,
      });
      setAllRows(prev => prev.map(r => r.id === row.id ? updated : r));
    } catch (e) { alert('Kayıt hatası: ' + e.message); }
    finally { setSavingId(null); }
  }

  function parseCSV(line) {
    const result = []; let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if ((ch === ';' || ch === ',') && !inQ) { result.push(cur); cur = ''; }
      else cur += ch;
    }
    result.push(cur);
    return result;
  }

  async function handleCSV(e) {
    if (!filter) { alert('CSV yüklemek için önce bir kaynak filtrele'); return; }
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true); setImportMsg(null);
    try {
      const text  = await file.text();
      const lines = text.replace(/\r/g, '').split('\n').filter(l => l.trim());
      if (lines.length < 2) throw new Error('CSV boş');
      let ok = 0;
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCSV(lines[i]);
        const offerId = cols[0]?.trim();
        const name    = cols[1]?.trim() || '';
        if (!offerId) continue;
        const exists = allRows.find(r => r.counterpart_offer_id === offerId && r.source_name === filter);
        if (!exists) { await api('POST', '/supply-map', { source_name: filter, counterpart_offer_id: offerId, counterpart_name: name }); ok++; }
      }
      await loadAll();
      setImportMsg({ ok, text: `${ok} eşleşme eklendi.` });
    } catch (e) { setImportMsg({ ok: -1, text: 'Hata: ' + e.message }); }
    finally { setImporting(false); }
  }

  async function deleteRow(id) {
    await api('DELETE', `/supply-map/${id}`);
    setAllRows(prev => prev.filter(r => r.id !== id));
  }

  async function addRow() {
    const src = filter || (sources.length === 1 ? sources[0].name : null);
    if (!src) { alert('Önce bir kaynak seç (filtrele)'); return; }
    if (!addForm?.counterpart_offer_id) { alert('Karşı taraf ürün kodu zorunlu'); return; }
    setAddSaving(true);
    try {
      await api('POST', '/supply-map', {
        source_name:          src,
        counterpart_offer_id: addForm.counterpart_offer_id.trim(),
        counterpart_name:     addForm.counterpart_name?.trim() || null,
        my_offer_id:          addForm.my_offer_id || null,
        my_name:              mySkus.find(s => s.offer_id === addForm.my_offer_id)?.name || null,
      });
      await loadAll();
      setAddForm(null);
    } catch (e) { alert('Hata: ' + e.message); }
    finally { setAddSaving(false); }
  }

  function downloadCSV() {
    const lines = ['Kaynak;Karşı Taraf Kodu;Karşı Taraf Adı;Benim Ürün Kodum;Benim Ürün Adım'];
    rows.forEach(r => {
      lines.push([r.source_name || '', r.counterpart_offer_id || '', r.counterpart_name || '', r.my_offer_id || '', r.my_name || '']
        .map(v => `"${String(v).replace(/"/g,'""')}"`).join(';'));
    });
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `sku-map-${filter || 'tumu'}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); setTimeout(() => URL.revokeObjectURL(url), 60000);
  }

  const addFormSource = filter || (sources.length === 1 ? sources[0].name : null);

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <p className="text-sm text-gray-500 flex-1">Kaynak bazlı ürün eşleştirmeleri</p>
        <div className="flex gap-1.5 flex-wrap">
          <button onClick={() => setFilter(null)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
              filter === null ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
            }`}>
            Tümü {filter === null && allRows.length > 0 && `(${allRows.length})`}
          </button>
          {sources.map(s => {
            const cnt = allRows.filter(r => r.source_name === s.name).length;
            return (
              <button key={s.id} onClick={() => setFilter(filter === s.name ? null : s.name)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  filter === s.name ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}>
                {s.name} {cnt > 0 && <span className="ml-1 opacity-70">({cnt})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Eşleşme durumu filtresi */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
          {[
            { key: 'matched', label: `Eşleşenler`, count: matchedCount, color: 'text-green-700' },
            { key: 'missing', label: `Eksikler`,   count: missingCount, color: 'text-amber-600' },
            { key: 'all',     label: 'Tümü',       count: sourceFiltered.length, color: 'text-gray-600' },
          ].map(opt => (
            <button key={opt.key} onClick={() => setMatchFilter(opt.key)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                matchFilter === opt.key ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'
              }`}>
              {opt.label}
              {opt.count > 0 && (
                <span className={`ml-1.5 text-[10px] font-bold ${matchFilter === opt.key ? opt.color : 'text-gray-400'}`}>
                  {opt.count}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button onClick={() => fileRef.current?.click()} disabled={importing}
          className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:border-blue-300 text-gray-700 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
          title={!filter ? 'Filtreleyerek kaynak seç' : ''}>
          {importing ? <Spinner sm /> : '⬆'} CSV Yükle
        </button>
        <button onClick={downloadCSV}
          className="flex items-center gap-1.5 text-xs bg-white border border-gray-200 hover:border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg transition-colors">
          ⬇ CSV İndir
        </button>
        <button onClick={() => setAddForm({ counterpart_offer_id: '', counterpart_name: '', my_offer_id: '' })}
          className="flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors">
          + Satır Ekle
        </button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleCSV} />
      </div>

          {/* Manuel ekleme formu */}
          {addForm && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <p className="text-xs font-semibold text-blue-700 mb-3">Yeni eşleşme ekle — {active}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{active}'in Ürün Kodu *</label>
                  <input
                    autoFocus
                    value={addForm.counterpart_offer_id}
                    onChange={e => setAddForm(f => ({ ...f, counterpart_offer_id: e.target.value }))}
                    placeholder="örn. ADM-001"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-white font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">{addFormSource}'in Ürün Adı</label>
                  <input
                    value={addForm.counterpart_name}
                    onChange={e => setAddForm(f => ({ ...f, counterpart_name: e.target.value }))}
                    placeholder="isteğe bağlı"
                    className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-blue-400 bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Benim Ürünüm (Ozon)</label>
                  <ProductSelect
                    value={addForm.my_offer_id}
                    onChange={val => setAddForm(f => ({ ...f, my_offer_id: val }))}
                    mySkus={mySkus}
                    placeholder="Ürün seç (isteğe bağlı)"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setAddForm(null)}
                  className="border border-gray-200 text-gray-500 rounded-lg px-3 py-1.5 text-xs hover:bg-white">
                  İptal
                </button>
                <button onClick={addRow} disabled={addSaving}
                  className="bg-blue-600 text-white rounded-lg px-4 py-1.5 text-xs hover:bg-blue-700 disabled:opacity-50">
                  {addSaving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </div>
          )}

      {importMsg && (
        <div className={`mb-4 text-xs px-3 py-2 rounded-lg border flex items-center justify-between ${
          importMsg.ok < 0 ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'
        }`}>
          <span>{importMsg.text}</span>
          <button onClick={() => setImportMsg(null)} className="ml-3 opacity-50 hover:opacity-100">✕</button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white border border-gray-100 rounded-2xl">
          <div className="text-4xl mb-3">🔗</div>
          <p className="text-sm font-medium">Henüz eşleşme yok</p>
          <p className="text-xs mt-1">Gelen talepleri onaylayınca otomatik eklenir<br/>ya da "+ Satır Ekle" ile manuel ekle</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {!filter && <th className="text-left px-4 py-3 font-semibold text-gray-500 w-20">Kaynak</th>}
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Karşı Taraf Ürünü</th>
                <th className="text-center px-2 py-3 text-gray-300 w-8">↔</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500">Benim Ürünüm (Ozon)</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => {
                const bothFilled = row.my_offer_id && row.counterpart_offer_id;
                return (
                  <tr key={row.id} className={`border-b border-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/30'} ${bothFilled ? 'hover:bg-green-50/30' : 'hover:bg-yellow-50/30'}`}>
                    {!filter && (
                      <td className="px-4 py-2.5">
                        <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{row.source_name}</span>
                      </td>
                    )}
                    <td className="px-4 py-2">
                      <CounterpartCell row={row} onSave={updated => setAllRows(prev => prev.map(r => r.id === updated.id ? updated : r))} />
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <span className={`text-sm ${bothFilled ? 'text-green-400' : 'text-gray-200'}`}>↔</span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1">
                          <ProductSelect value={row.my_offer_id || ''} onChange={val => saveMyOfferId(row, val)} mySkus={mySkus} />
                          {row.my_name && row.my_offer_id && <div className="text-gray-400 text-xs truncate mt-0.5 px-1">{row.my_name}</div>}
                        </div>
                        {savingId === row.id && <Spinner sm />}
                      </div>
                    </td>
                    <td className="px-2 py-2.5">
                      <button onClick={() => deleteRow(row.id)} className="text-gray-300 hover:text-red-400 transition-colors p-1" title="Sil">✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-gray-400 mt-3">Dropdown'dan seçince otomatik kaydedilir · Gelen talep onaylandığında yeni eşleşmeler otomatik eklenir</p>
    </div>
  );
}

// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// TAB 4: STOK KUYRUĞU
// ─────────────────────────────────────────────

function StokKuyrugu() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});

  const load = useCallback(async () => {
    try {
      const data = await api('GET', '/stock-queue?limit=200');
      setJobs(Array.isArray(data) ? data : []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  async function handleRetry(id) {
    setRetrying(r => ({ ...r, [id]: true }));
    try {
      await api('POST', `/stock-queue/${id}/retry`);
      await load();
    } catch (e) { alert(e.message); }
    setRetrying(r => ({ ...r, [id]: false }));
  }

  const counts = {
    pending:    jobs.filter(j => j.status === 'pending').length,
    processing: jobs.filter(j => j.status === 'processing').length,
    done:       jobs.filter(j => j.status === 'done').length,
    failed:     jobs.filter(j => j.status === 'failed').length,
  };

  function StatusBadge({ status }) {
    const map = {
      pending:    { label: 'Sırada',     cls: 'bg-yellow-100 text-yellow-800' },
      processing: { label: 'İşleniyor',  cls: 'bg-blue-100 text-blue-800 animate-pulse' },
      done:       { label: '✅ Başarılı', cls: 'bg-green-100 text-green-800' },
      failed:     { label: '❌ Başarısız',cls: 'bg-red-100 text-red-800' },
    };
    const s = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
    return <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
  }

  if (loading) return <div className="flex justify-center py-12"><Spinner /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-3 text-sm">
          {counts.processing > 0 && <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">🔄 {counts.processing} işleniyor</span>}
          {counts.pending > 0    && <span className="bg-yellow-50 text-yellow-700 px-3 py-1 rounded-full font-medium">⏳ {counts.pending} sırada</span>}
          {counts.failed > 0     && <span className="bg-red-50 text-red-700 px-3 py-1 rounded-full font-medium">❌ {counts.failed} başarısız</span>}
          {counts.done > 0       && <span className="bg-green-50 text-green-700 px-3 py-1 rounded-full font-medium">✅ {counts.done} tamamlandı</span>}
        </div>
        <button onClick={load} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5 border border-gray-200 rounded-lg">↻ Yenile</button>
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-16 text-gray-400">Kuyrukta iş yok</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">Ürün</th>
                <th className="px-4 py-3 text-center">Düşülen</th>
                <th className="px-4 py-3 text-center">Gönderilen Stok</th>
                <th className="px-4 py-3 text-center">Doğrulanan</th>
                <th className="px-4 py-3 text-center">Deneme</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-left">Eklenme</th>
                <th className="px-4 py-3 text-left">Hata</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {jobs.map(j => (
                <tr key={j.id} className={j.status === 'failed' ? 'bg-red-50' : j.status === 'done' ? 'bg-green-50/40' : ''}>
                  <td className="px-4 py-3 text-gray-400 text-xs">{j.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{j.offer_id}</div>
                    {j.product_name && <div className="text-xs text-gray-400 truncate max-w-[200px]">{j.product_name}</div>}
                    {j.source_name  && <div className="text-xs text-blue-500">{j.source_name}</div>}
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-gray-700">-{j.qty}</td>
                  <td className="px-4 py-3 text-center font-mono">
                    {j.sent_stock != null ? <span className="text-blue-700 font-semibold">{j.sent_stock}</span> : '—'}
                  </td>
                  <td className="px-4 py-3 text-center font-mono">
                    {j.verified_stock != null ? (
                      j.verified_stock === j.sent_stock
                        ? <span className="text-green-700 font-semibold">{j.verified_stock} ✓</span>
                        : <span className="text-red-600 font-semibold">{j.verified_stock} ✗</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-500">{j.attempts}/3</td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={j.status} /></td>
                  <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{fmtDt(j.queued_at)}</td>
                  <td className="px-4 py-3 text-xs text-red-600 max-w-[180px]">{j.last_error || ''}</td>
                  <td className="px-4 py-3">
                    {j.status === 'failed' && (
                      <button
                        onClick={() => handleRetry(j.id)}
                        disabled={retrying[j.id]}
                        className="px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 whitespace-nowrap"
                      >
                        {retrying[j.id] ? '...' : '↻ Tekrar Dene'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ANA SAYFA
// ─────────────────────────────────────────────

const TABS = [
  { key: 'giden',  label: 'Giden Talepler',  icon: '📬' },
  { key: 'gelen',  label: 'Gelen Talepler',  icon: '📩' },
  { key: 'sku',    label: 'SKU Eşleştirme',  icon: '🔗' },
];

export default function SupplyManagement() {
  const [tab,          setTab]          = useState('gelen');
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('ozon_token');
    fetch('/api/incoming-requests?status=pending', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(d => setPendingCount(Array.isArray(d) ? d.length : 0))
      .catch(() => {});
  }, []);

  return (
    <div className="p-4 md:p-6 max-w-full">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Tedarik Yönetimi</h1>
      </div>

      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {[
          { key: 'gelen', label: "Gelen Talepler", icon: '📩' },
          { key: 'giden', label: "Giden Talepler", icon: '📬' },
          { key: 'sku',   label: 'SKU Eşleştirme', icon: '🔗' },
          { key: 'kuyruk', label: 'Stok Kuyruğu', icon: '📋' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}>
            <span>{t.icon}</span>
            {t.label}
            {t.key === 'gelen' && pendingCount > 0 && (
              <span className="ml-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'gelen' && <GelenTalepler onPendingChange={setPendingCount} />}
      {tab === 'giden' && <GidenTalepler />}
      {tab === 'sku'   && <SkuEslestirme />}
      {tab === 'kuyruk' && <StokKuyrugu />}
    </div>
  );
}
